<script setup>
import { ref, onMounted } from 'vue';
import { Link } from '@inertiajs/vue3';

const show = ref(false);
const cookiesAccepted = ref(false);

// Function to check if cookie exists
const hasCookie = (name) => {
    return document.cookie.split(';').some((item) => item.trim().startsWith(`${name}=`));
};

// Function to get cookie value
const getCookie = (name) => {
    const cookieValue = document.cookie
        .split('; ')
        .find(row => row.startsWith(`${name}=`))
        ?.split('=')[1];
    
    if (cookieValue) {
        try {
            return JSON.parse(decodeURIComponent(cookieValue));
        } catch (e) {
            console.error('Error parsing cookie:', e);
            return null;
        }
    }
    return null;
};

// Function to set cookie with preferences
const setCookiePreferences = (preferences) => {
    // Set expiry to 6 months
    const expiryDate = new Date();
    expiryDate.setMonth(expiryDate.getMonth() + 6);
    
    // Set cookie with preferences
    const cookieValue = encodeURIComponent(JSON.stringify(preferences));
    document.cookie = `cookiePreferences=${cookieValue};expires=${expiryDate.toUTCString()};path=/;SameSite=Lax`;
    
    // Update UI state
    cookiesAccepted.value = preferences.marketing || preferences.analytics;
    show.value = false;
    
    // Initialize cookies based on preferences
    initializeCookies(preferences);
};

// Function to accept all cookies
const acceptAll = () => {
    const preferences = {
        necessary: true,
        functional: true,
        analytics: true,
        marketing: true
    };
    setCookiePreferences(preferences); // Ensure preferences are saved
    show.value = false; // Close the popup
};

// Function to decline non-essential cookies
const acceptNecessaryOnly = () => {
    const preferences = {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false
    };
    setCookiePreferences(preferences); // Ensure preferences are saved
    show.value = false; // Close the popup
};

// Function to initialize cookies (only called after acceptance)
const initializeCookies = (preferences) => {
    // Initialize your cookies, analytics, etc. based on preferences
    
    // You could initialize different tracking scripts here based on the preferences
    // For example:
    // if (preferences.analytics) {
    //     initializeAnalytics();
    // }
    // if (preferences.marketing) {
    //     initializeMarketing();
    // }
};

onMounted(() => {
    // Check if user has already made a choice
    if (hasCookie('cookiePreferences')) {
        const preferences = getCookie('cookiePreferences');
        if (preferences) {
            cookiesAccepted.value = preferences.marketing || preferences.analytics;
            initializeCookies(preferences);
        } else {
            show.value = true;
        }
    } else {
        // Show the banner if no choice has been made
        show.value = true;
    }

    // Listen for the close-cookie-popup event
    window.addEventListener('close-cookie-popup', () => {
        show.value = false;
    });
});
</script>

<template>
  <div v-if="show" v-bind="$attrs" class="cookie-consent-wrapper">
    <div class="cookie-banner">
      <div class="cookie-content">
        <h5>Vi bruger cookies</h5>
        <p>
          Denne hjemmeside bruger cookies til at forbedre din oplevelse. 
          Du kan vælge hvilke cookies du vil tillade.
        </p>
        
        <div class="cookie-actions">
          <button @click="acceptAll" class="btn btn-primary btn-sm">Accepter alle</button>
          <button @click="acceptNecessaryOnly" class="btn btn-outline-secondary btn-sm mx-2">Kun nødvendige</button>
          <Link as="button" :href="route('cookie.preferences')" class="btn btn-link btn-sm" @close-cookie-popup="show = false">
            Tilpas indstillinger
          </Link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { router } from '@inertiajs/vue3';
import CookieService from '@/Services/CookieService';

export default {
  inheritAttrs: false,
  setup() {
    const show = ref(false);
    
    onMounted(() => {
      // Show banner if no cookie preferences are set
      show.value = !CookieService.hasConsent();
    });
    
    const acceptAll = () => {
      const preferences = {
        necessary: true,
        functional: true,
        analytics: true,
        marketing: true
      };
      
      CookieService.saveCookiePreferences(preferences);
      show.value = false;
    };
    
    const acceptNecessaryOnly = () => {
      const preferences = {
        necessary: true,
        functional: false,
        analytics: false,
        marketing: false
      };
      
      CookieService.saveCookiePreferences(preferences);
      show.value = false;
    };
    
    const showPreferences = () => {
      router.visit(route('cookie.preferences'));
    };
    
    return {
      show,
      acceptAll,
      acceptNecessaryOnly,
      showPreferences
    };
  }
}
</script>

<style scoped>
.cookie-consent-wrapper {
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  right: 1rem;
  z-index: 9999;
}

.cookie-banner {
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 1px 3px rgba(0, 0, 0, 0.08);
  padding: 1.25rem;
  max-width: 500px;
  margin: 0 auto;
}

.cookie-content h5 {
  margin-top: 0;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.cookie-content p {
  margin-bottom: 1rem;
  font-size: 0.9rem;
}

.cookie-content h5, .cookie-content p {
  color: black; /* Ensure text is visible on white background */
}

.cookie-actions {
  display: flex;
  align-items: center;
}

@media (max-width: 576px) {
  .cookie-actions {
    flex-direction: column;
    align-items: stretch;
  }
  
  .cookie-actions button {
    margin: 0.25rem 0;
  }
}
</style>
